import { routes } from "@bezoned/shared/routes.js";
import type { ChannelTab } from "@bezoned/shared/types/api/channel/channel-tab-list.types.js";
import { app, call, chat, meeting, stageView } from "@microsoft/teams-js";
import { useCallback, useContext, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../features/auth/use-auth.js";
import { OfficeContext } from "../features/office/office-context.js";
import {
  getOnboardingJourneyId,
  ONBOARDING_JOURNEY_ID_QUERY_PARAM,
} from "../features/onboarding/onboarding-tracker.js";
import {
  HostApplication,
  Platform,
  TeamsClientVariant,
} from "../features/platform-detection/detect-platform.js";
import { usePlatform } from "../features/platform-detection/use-platform.js";
import { env } from "../utils/env.js";
import { recordErrorInSentry } from "../utils/init-sentry.js";
import { updateSearchParams } from "../utils/url-search-params.js";

type Tabs = Record<string, ChannelTab[]>;
type TabData = Tabs[keyof Tabs][number];

interface UseTeamsNavigationProps {
  onCompleted?: () => void;
}

export function useTeamsNavigation(props?: UseTeamsNavigationProps) {
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>(
    {}
  );
  const {
    safeOperation,
    getPlatformContext,
    platform,
    teamsClientVariant,
    hostApplication,

    isTeamsDesktopClient,
    isTeamsWebClient,
  } = usePlatform();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fallbackMsTeamId = new URLSearchParams(location.search).get("msTeamId");
  const officeContext = useContext(OfficeContext);
  const team = officeContext?.team ?? null;

  const setLoading = useCallback((key: string, loading: boolean) => {
    setLoadingStates((prev) => ({ ...prev, [key]: loading }));
  }, []);

  const isLoading = useCallback(
    (key: string) => loadingStates[key] ?? false,
    [loadingStates]
  );

  /**
   * Opens a new browser tab with the given URL.
   * @param {string} url - The URL to open.
   * @param {boolean} transform - Whether to transform the URL to a browser URL.
   * @returns {boolean} True if the tab was opened, false otherwise.
   */
  const openInNewBrowserTab = useCallback((url: string, _transform = true) => {
    // ! We used to open browser tabs/windows, which were handled by the browser teams version.
    // ! During the review, MS representatives told us, we cannot start new Teams instances.
    // ! This is a short term fix, without transforming the URL for the browser, it will always try to open the
    // ! desktop app.
    // TODO: Revisit this after the app is approved and discuss, what to do next.
    // const browserUrl = transform ? transformToBrowserUrl(url) : url;
    const browserUrl = url;

    // return window.open(browserUrl, '_blank', "width=1280,height=728,toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes") !== null;
    return window.open(browserUrl, "_blank") !== null;
  }, []);

  const openDeepLink = useCallback(
    async (
      stageViewConfig: Parameters<typeof stageView.open>[0] | null,
      fallbackUrl: string
    ) => {
      const context = await getPlatformContext();

      // When opened in a side panel, we need to open the link in a new window.
      // We would otherwise use the stage view, as it's more stable.
      // However stageView is not supported in side panel showing following error:
      // this call is only allowed in following contexts: ["content"]. Current context: "sidePanel".
      const inStandardTeamsView = context?.page?.frameContext === "content";

      // In web version of Teams (teams.microsoft.com) we can't use stageView.open. It is supported, but
      // instead of opening new windows (or at least tabs) it opens "modal" on top of the current view.
      // This means only one stageView can be displayed at a time.
      // Instead, we decided that on web, we use tabs to open the links.
      const runningInWebTeams = context?.app?.host?.clientType === "web";
      const runningInMobileOutlook =
        platform === Platform.Microsoft &&
        hostApplication === HostApplication.Outlook &&
        teamsClientVariant === TeamsClientVariant.Mobile;

      if (runningInWebTeams || runningInMobileOutlook) {
        return openInNewBrowserTab(fallbackUrl, !runningInMobileOutlook);
      }

      if (
        !(stageViewConfig && stageView.isSupported() && inStandardTeamsView)
      ) {
        // ! This may open in the same view if the app is docked, but we don't have any choice
        app.openLink(fallbackUrl);
        return true;
      }

      await stageView.open(stageViewConfig);
      return true;
    },
    [
      getPlatformContext,
      hostApplication,
      openInNewBrowserTab,
      platform,
      teamsClientVariant,
    ]
  );

  const navigateToChannelTab = useCallback(
    async (channelId: string, tabData: TabData) => {
      setLoading(tabData.id, true);

      await safeOperation(
        () => {
          // When navigating to a file tab, we need to use app.openLink instead of stageView.open
          // This is because the contentUrl of File tab contains placeholders, instead of final URL we can open.
          // Because we don't know what "content" to put instead of the placeholders, we use webUrl, which
          // in exchange cannot be used in stageView, because it leads to teams.microsoft.com. Any site starting with
          // that domain is "not embeddable" and stageview will error out.
          // The only option is to use app.openLink, which may open the tab in the same view (closing the app), but
          // at least you are navigated somewhere.
          const contentUrl = tabData.contentUrl ?? tabData.webUrl;
          const entityId = tabData.entityId ?? tabData.id;

          const config = {
            appId: tabData.appId,
            contentUrl,
            entityId,
            openMode: stageView.StageViewOpenMode.popout,
            threadId: channelId,
            title: tabData.displayName,
          };

          return openDeepLink(config, tabData.webUrl);
        },
        () => openInNewBrowserTab(tabData.webUrl)
      );

      setLoading(tabData.id, false);
      props?.onCompleted?.();
    },
    [setLoading, safeOperation, props, openDeepLink, openInNewBrowserTab]
  );

  const navigateToChannel = useCallback(
    async (msChannelId: string, displayName = "conversations") => {
      const groupId = team?.msTeamId ?? fallbackMsTeamId;
      if (!groupId) {
        return;
      }

      const channelUrl = `https://teams.microsoft.com/l/channel/${msChannelId}/${encodeURIComponent(displayName)}?groupId=${groupId}&tenantId=${user?.tenantId}`;
      setLoading(msChannelId, true);

      await safeOperation(
        async () => {
          // We are not using stageView config, because any URL that starts with teams.microsoft.com is not embeddable.
          await openDeepLink(null, channelUrl);
          return true;
        },
        () => openInNewBrowserTab(channelUrl)
      );

      setLoading(msChannelId, false);
      props?.onCompleted?.();
    },
    [
      safeOperation,
      team,
      fallbackMsTeamId,
      props,
      openDeepLink,
      openInNewBrowserTab,
      setLoading,
      user?.tenantId,
    ]
  );

  const navigateToTeam = async ({
    channelId,
    teamId,
    tenantId,
  }: {
    channelId: string;
    teamId: string;
    tenantId: string;
  }) => {
    const teamUrl = `https://teams.microsoft.com/l/team/${encodeURIComponent(channelId)}/conversations?groupId=${encodeURIComponent(teamId)}&tenantId=${encodeURIComponent(tenantId)}`;
    setLoading(teamId, true);

    try {
      await safeOperation(
        () => openDeepLink(null, teamUrl),
        () => openInNewBrowserTab(teamUrl)
      );
      props?.onCompleted?.();
    } finally {
      setLoading(teamId, false);
    }
  };

  const navigateToChat = useCallback(
    async (
      tenantId: string,
      users: string[],
      message?: string,
      options?: { forceDeepLink?: boolean }
    ) => {
      const chatUrl = `https://teams.microsoft.com/l/chat/0/0?tenantId=${tenantId}&users=${users.join(",")}&message=${message ?? " "}`;

      setLoading(chatUrl, true);

      await safeOperation(
        async () => {
          // When forceDeepLink is set, bypass the Teams SDK and use the deep link URL directly.
          // This ensures the Teams window is brought to the foreground on Windows,
          // which doesn't happen when using chat.openGroupChat() from an external process.
          if (options?.forceDeepLink) {
            await openDeepLink(null, chatUrl);
            return true;
          }

          const context = await getPlatformContext();

          // In web version of Teams (teams.microsoft.com) we can't use chat.openGroupChat. It is supported, but
          // instead of opening new window or tab it opens in the same view effectively closing the app.
          // Instead, we decided that on web, we use tabs to open the links.
          const runningInWebTeams = context?.app?.host?.clientType === "web";
          const runningInMobileOutlook =
            platform === Platform.Microsoft &&
            hostApplication === HostApplication.Outlook &&
            teamsClientVariant === TeamsClientVariant.Mobile;

          if (runningInWebTeams || runningInMobileOutlook) {
            return openInNewBrowserTab(chatUrl, !runningInMobileOutlook);
          }

          if (chat.isSupported()) {
            return chat.openGroupChat({ message: message ?? " ", users });
          }
          await openDeepLink(null, chatUrl);
          return true;
        },
        () => openInNewBrowserTab(chatUrl)
      );

      setLoading(chatUrl, false);
      props?.onCompleted?.();
    },
    [
      setLoading,
      safeOperation,
      props,
      getPlatformContext,
      openInNewBrowserTab,
      platform,
      hostApplication,
      teamsClientVariant,
      openDeepLink,
    ]
  );

  const navigateToCall = useCallback(
    async (users: string[], options?: { forceDeepLink?: boolean }) => {
      const callUrl = `https://teams.microsoft.com/l/call/0/0?users=${users.join(",")}&withVideo=true`;

      setLoading(callUrl, true);

      await safeOperation(
        async () => {
          // When forceDeepLink is set, bypass the Teams SDK and use the deep link URL directly.
          // This ensures the Teams window is brought to the foreground on Windows,
          // which doesn't happen when using call.startCall() from an external process.
          if (options?.forceDeepLink) {
            await openDeepLink(null, callUrl);
            return true;
          }

          const context = await getPlatformContext();

          // In web version of Teams (teams.microsoft.com) we can't use call.startCall. It is supported, but
          // instead of opening new window or tab it opens in the same view effectively closing the app.
          // Instead, we decided that on web, we use tabs to open the links.
          const runningInWebTeams = context?.app?.host?.clientType === "web";
          const runningInMobileOutlook =
            platform === Platform.Microsoft &&
            hostApplication === HostApplication.Outlook &&
            teamsClientVariant === TeamsClientVariant.Mobile;

          if (runningInWebTeams || runningInMobileOutlook) {
            return openInNewBrowserTab(callUrl, !runningInMobileOutlook);
          }

          if (call.isSupported()) {
            return await call.startCall({
              requestedModalities: [call.CallModalities.Audio],
              source: "bezoned",
              targets: users,
            });
          }

          await openDeepLink(null, callUrl);
          return true;
        },
        () => openInNewBrowserTab(callUrl)
      );

      setLoading(callUrl, false);
      props?.onCompleted?.();
    },
    [
      getPlatformContext,
      openInNewBrowserTab,
      platform,
      hostApplication,
      teamsClientVariant,
      openDeepLink,
      setLoading,
      props,
      safeOperation,
    ]
  );

  const navigateToRoomMeeting = useCallback(
    async (url: string) => {
      setLoading(url, true);

      await safeOperation(
        async () => {
          const context = await getPlatformContext();

          // In web version of Teams (teams.microsoft.com) we can't use meeting.joinMeeting. It is supported, but
          // instead of opening new window or tabs it in the same view - effectively closing the app
          // Instead, we decided that on web, we use tabs to open the links.
          const runningInWebTeams = context?.app?.host?.clientType === "web";
          const runningInMobileOutlook =
            platform === Platform.Microsoft &&
            hostApplication === HostApplication.Outlook &&
            teamsClientVariant === TeamsClientVariant.Mobile;

          if (runningInWebTeams || runningInMobileOutlook) {
            return openInNewBrowserTab(url, !runningInMobileOutlook);
          }

          try {
            const joinWebUrl = URL.parse(url);
            if (!joinWebUrl) {
              throw new Error("Invalid meeting URL");
            }

            // Why keeping the API consistent, right? For some reason, meeting does not have .isSupported() method.
            // We have to try and if it fails, we know it's not supported :)
            await meeting.joinMeeting({
              joinWebUrl,
            });

            return true;
          } catch (error) {
            recordErrorInSentry(error);
            await openDeepLink(null, url);
            return true;
          }
        },
        () => openInNewBrowserTab(url)
      );

      setLoading(url, false);
      props?.onCompleted?.();
    },
    [
      setLoading,
      safeOperation,
      props,
      getPlatformContext,
      openInNewBrowserTab,
      platform,
      hostApplication,
      teamsClientVariant,
      openDeepLink,
    ]
  );

  /**
   * This function is used to open the office view in the best possible way for the current platform.
   * - teams desktop -> new window, so they can still access chat and other teams functionality
   * - teams web -> same page
   * - web -> same page
   */
  const navigateToOffice = useCallback(
    async ({
      msTeamId,
      forceSameWindow,
    }: {
      // ID Of the team, used for URL query
      msTeamId: string;
      // Whether to force redirect in the same window
      forceSameWindow?: boolean;
    }) => {
      const journeyId = await getOnboardingJourneyId();
      const search = updateSearchParams(location.search, {
        remove: [
          "edit",
          "error",
          "granted",
          ONBOARDING_JOURNEY_ID_QUERY_PARAM,
          "step",
          "teamId",
        ],
        set: {
          [ONBOARDING_JOURNEY_ID_QUERY_PARAM]: journeyId,
          teamId: msTeamId,
        },
      });
      const stageSearch = updateSearchParams(search, {
        set: { stage: "true" },
      });

      await safeOperation(
        async () => {
          // Remove the : isTeamsWebClient from here once the code from below is un-commented
          if (forceSameWindow || isTeamsWebClient()) {
            return navigate(
              { pathname: routes.protected.office, search },
              { replace: true }
            );
          }

          // We can put this back if we decide that we want to again open Offices in new tab
          // This change was made for the MS review process.
          //
          // if (isTeamsWebClient()) {
          //   console.log("navigateToOffice, using window.open for web teams");
          //   // We need to add team data to the URL, because the local storage is not shared between the current and
          //   // new window.
          //   // See: withFluidFramework
          //   window.open(
          //     `${env.PUBLIC_FRONTEND_URL}${routes.protected.office}${stageSearch}`,
          //     "_blank"
          //   );
          //   return;
          // }

          if (isTeamsDesktopClient() && stageView.isSupported()) {
            console.log(
              "navigateToOffice, using stageView.open for teams desktop"
            );
            const context = await getPlatformContext();

            if (!context) {
              console.error(
                "Failed to read platform context. Will use fallback navigation."
              );
              throw new Error("Failed to read platform context");
            }

            if (!context.app.appId) {
              console.error(
                "Failed to read Teams app ID from platform context. Will use fallback navigation."
              );
              throw new Error(
                "Failed to read Teams app ID from platform context"
              );
            }

            await stageView.open({
              appId: String(context.app.appId),
              contentUrl: `${env.PUBLIC_FRONTEND_URL}${routes.protected.office}${stageSearch}`,
              openMode: stageView.StageViewOpenMode.popout,
              title: "BeZoned",
              websiteUrl: `${env.PUBLIC_FRONTEND_URL}${routes.protected.office}${stageSearch}`,
            });
            return;
          }

          return navigate(
            { pathname: routes.protected.office, search },
            { replace: true }
          );
        },
        () => {
          console.log(
            "navigateToOffice, using navigate for outside of Teams platform"
          );

          navigate(
            { pathname: routes.protected.office, search },
            { replace: true }
          );
          return Promise.resolve();
        }
      );
    },
    [
      safeOperation,
      navigate,
      location.search,
      isTeamsWebClient,
      isTeamsDesktopClient,
      getPlatformContext,
    ]
  );

  return {
    isLoading,
    navigateToCall,
    navigateToChannel,
    navigateToChannelTab,
    navigateToChat,
    navigateToOffice,
    navigateToRoomMeeting,
    navigateToTeam,
  };
}
